import { useState, useMemo } from 'react';
import { type IntegrationDefinition, type IntegrationConnection, type IntegrationCategory, type AccessFinancialsConfig } from '@/types/integrations';
import { INTEGRATION_DEFINITIONS, CATEGORY_LABELS } from '@/lib/integrations-data';
import { IntegrationCard } from '@/components/integrations/integration-card';
import { StandardConnectionDialog } from '@/components/integrations/standard-connection-dialog';
import { OAuthConnectionDialog } from '@/components/integrations/oauth-connection-dialog';
import { AccessFinancialsDialog } from '@/components/integrations/access-financials-dialog';
import { IntegrationSettingsDialog } from '@/components/integrations/integration-settings-dialog';
import { DisconnectConfirmationDialog } from '@/components/integrations/disconnect-confirmation-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

// Mock companies for multi-tenant support
const MOCK_COMPANIES = [
  { id: 'company-1', name: 'Acme Corporation' },
  { id: 'company-2', name: 'TechStart Inc.' },
  { id: 'company-3', name: 'Global Solutions Ltd.' },
];

// Mock connections data
const MOCK_CONNECTIONS: IntegrationConnection[] = [
  {
    id: 'conn-1',
    integrationId: 'access-financials',
    companyId: 'company-1',
    status: 'connected',
    credentials: { serverUrl: 'https://server.acme.com', database: 'AcmeDB', username: 'admin' },
    lastSync: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  },
  {
    id: 'conn-2',
    integrationId: 'outlook',
    companyId: 'company-1',
    status: 'connected',
    credentials: {},
    connectedAccount: 'user@acme.com',
    lastSync: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  },
  {
    id: 'conn-3',
    integrationId: 'stripe',
    companyId: 'company-2',
    status: 'connected',
    credentials: { publishableKey: 'xxx', secretKey: 'xxx' },
    lastSync: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  },
];

export function IntegrationsPage() {
  const [selectedCompany, setSelectedCompany] = useState(MOCK_COMPANIES[0].id);
  const [connections, setConnections] = useState<IntegrationConnection[]>(MOCK_CONNECTIONS);
  const [selectedCategory, setSelectedCategory] = useState<IntegrationCategory>('financial');

  // Dialog states
  const [connectingIntegration, setConnectingIntegration] = useState<IntegrationDefinition | null>(null);
  const [configuringIntegration, setConfiguringIntegration] = useState<IntegrationDefinition | null>(null);
  const [configuringConnection, setConfiguringConnection] = useState<IntegrationConnection | null>(null);
  const [disconnectingIntegration, setDisconnectingIntegration] = useState<IntegrationDefinition | null>(null);
  const [disconnectingConnection, setDisconnectingConnection] = useState<IntegrationConnection | null>(null);

  // Get connections for selected company
  const companyConnections = useMemo(() => {
    return connections.filter(conn => conn.companyId === selectedCompany);
  }, [connections, selectedCompany]);

  // Get integrations by category with connection status
  const integrationsByCategory = useMemo(() => {
    const categories: Record<IntegrationCategory, IntegrationDefinition[]> = {
      financial: [],
      communication: [],
      credit: [],
      banking: [],
      data: [],
    };

    INTEGRATION_DEFINITIONS.forEach(integration => {
      categories[integration.category].push(integration);
    });

    return categories;
  }, []);

  // Hidden categories (for future consideration)
  const hiddenCategories: IntegrationCategory[] = ['credit', 'banking', 'data'];

  // Visible categories for tabs
  const visibleCategories = useMemo(() => {
    return Object.entries(CATEGORY_LABELS).filter(([key]) => !hiddenCategories.includes(key as IntegrationCategory));
  }, []);

  // Get connection for an integration
  const getConnection = (integrationId: string) => {
    return companyConnections.find(conn => conn.integrationId === integrationId);
  };

  // Handle connect
  const handleConnect = (integration: IntegrationDefinition) => {
    setConnectingIntegration(integration);
  };

  // Handle standard connection
  const handleStandardConnect = async (integrationId: string, credentials: Record<string, string>) => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const newConnection: IntegrationConnection = {
      id: `conn-${Date.now()}`,
      integrationId,
      companyId: selectedCompany,
      status: 'connected',
      credentials,
      lastSync: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };

    setConnections([...connections, newConnection]);
    toast.success('Integration connected successfully!');
  };

  // Handle OAuth connection
  const handleOAuthConnect = async (integrationId: string, account: string) => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const newConnection: IntegrationConnection = {
      id: `conn-${Date.now()}`,
      integrationId,
      companyId: selectedCompany,
      status: 'connected',
      credentials: {},
      connectedAccount: account,
      lastSync: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };

    setConnections([...connections, newConnection]);
    toast.success('Integration connected successfully!');
  };

  // Handle Access Financials connection
  const handleAccessFinancialsConnect = async (integrationId: string, config: AccessFinancialsConfig) => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const newConnection: IntegrationConnection = {
      id: `conn-${Date.now()}`,
      integrationId,
      companyId: selectedCompany,
      status: 'connected',
      credentials: {
        serverUrl: config.serverUrl,
        database: config.database,
        username: config.username,
      },
      config,
      lastSync: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };

    setConnections([...connections, newConnection]);
    toast.success('Access Financials connected successfully!');
  };

  // Handle configure
  const handleConfigure = (integration: IntegrationDefinition, connection: IntegrationConnection) => {
    setConfiguringIntegration(integration);
    setConfiguringConnection(connection);
  };

  // Handle reconnect
  const handleReconnect = (integration: IntegrationDefinition) => {
    setConfiguringIntegration(null);
    setConfiguringConnection(null);
    setConnectingIntegration(integration);
  };

  // Handle disconnect
  const handleDisconnect = (connection: IntegrationConnection) => {
    const integration = INTEGRATION_DEFINITIONS.find(i => i.id === connection.integrationId);
    if (integration) {
      setDisconnectingIntegration(integration);
      setDisconnectingConnection(connection);
    }
    setConfiguringIntegration(null);
    setConfiguringConnection(null);
  };

  // Handle disconnect confirm
  const handleDisconnectConfirm = async (connectionId: string, keepData: boolean) => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    setConnections(connections.filter(conn => conn.id !== connectionId));
    toast.success(
      keepData
        ? 'Integration disconnected. Historical data preserved.'
        : 'Integration disconnected. All data removed.'
    );
  };

  return (
    <div className="flex-1 space-y-6 p-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight">System Integrations</h1>
        </div>
        <p className="text-muted-foreground mt-2">
          Connect and manage third-party integrations for your organization
        </p>
      </div>

      {/* Company Selector */}
      {MOCK_COMPANIES.length > 1 && (
        <div className="flex items-center gap-4 max-w-xs">
          <Label htmlFor="company-select">Company:</Label>
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger id="company-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MOCK_COMPANIES.map(company => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Integration Categories Tabs */}
      <Tabs value={selectedCategory} onValueChange={(value) => setSelectedCategory(value as IntegrationCategory)}>
        <TabsList className="grid w-full grid-cols-2">
          {visibleCategories.map(([key, label]) => (
            <TabsTrigger key={key} value={key}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.entries(integrationsByCategory)
          .filter(([category]) => !hiddenCategories.includes(category as IntegrationCategory))
          .map(([category, integrations]) => (
            <TabsContent key={category} value={category} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {integrations.map(integration => (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    connection={getConnection(integration.id)}
                    onConnect={handleConnect}
                    onConfigure={handleConfigure}
                  />
                ))}
              </div>
            </TabsContent>
          ))}
      </Tabs>

      {/* Connection Dialogs */}
      {connectingIntegration?.id === 'access-financials' ? (
        <AccessFinancialsDialog
          integration={connectingIntegration}
          open={!!connectingIntegration}
          onClose={() => setConnectingIntegration(null)}
          onConnect={handleAccessFinancialsConnect}
        />
      ) : connectingIntegration?.requiresOAuth ? (
        <OAuthConnectionDialog
          integration={connectingIntegration}
          open={!!connectingIntegration}
          onClose={() => setConnectingIntegration(null)}
          onConnect={handleOAuthConnect}
        />
      ) : (
        <StandardConnectionDialog
          integration={connectingIntegration}
          open={!!connectingIntegration}
          onClose={() => setConnectingIntegration(null)}
          onConnect={handleStandardConnect}
        />
      )}

      {/* Settings Dialog */}
      <IntegrationSettingsDialog
        integration={configuringIntegration}
        connection={configuringConnection}
        open={!!configuringIntegration && !!configuringConnection}
        onClose={() => {
          setConfiguringIntegration(null);
          setConfiguringConnection(null);
        }}
        onReconnect={handleReconnect}
        onDisconnect={handleDisconnect}
      />

      {/* Disconnect Confirmation Dialog */}
      <DisconnectConfirmationDialog
        integration={disconnectingIntegration}
        connection={disconnectingConnection}
        open={!!disconnectingIntegration && !!disconnectingConnection}
        onClose={() => {
          setDisconnectingIntegration(null);
          setDisconnectingConnection(null);
        }}
        onConfirm={handleDisconnectConfirm}
      />
    </div>
  );
}
